import { useRef, useState } from 'react';
import { DateTime } from 'luxon';
import styled from 'styled-components';
import tw from 'twin.macro';
import { toast } from 'react-toastify';
import { Button } from '../../components/button';
import {
  Alert,
  Badge,
  Card,
  CardBody,
  CardHeader,
  Field,
  FormGrid,
  Input,
  Muted,
  Stack,
} from '../../components/ui';
import { MY_SERVICE_PROVIDER } from '../../services/serviceService/queries';
import {
  useGetPresignedUrlForServiceDocumentMutation,
  useMyServiceProviderQuery,
  useSubmitProviderInsuranceMutation,
  useSubmitWorkerBackgroundCheckMutation,
} from '../../services/serviceService/__generated__/queries.generated';
import { MoneyInput } from './serviceControls';
import { documentFileProblem, uploadServiceDocument } from './serviceDocuments';
import { serviceErrorMessage } from './serviceErrors';
import type { ProviderWorker } from './workerForm';

/**
 * Insurance (the Provider's) and the worker's background check (spec 019
 * D15). Both are badges a brand admin turns on after checking the file; a
 * handover needs a verified, unexpired background check. Files are private.
 */

const HiddenFileInput = styled.input`
  ${tw`hidden`}
`;

const FileRow = styled.div`
  ${tw`flex flex-wrap items-center gap-3`}
`;

const formatDate = (iso?: string | null) =>
  iso ? DateTime.fromISO(iso).toLocaleString(DateTime.DATE_MED) : '';

function DocumentPicker({
  file,
  onFile,
  error,
  testId,
}: {
  file: File | null;
  onFile: (file: File | null) => void;
  error?: string | null;
  testId: string;
}) {
  const input = useRef<HTMLInputElement>(null);
  return (
    <Field
      label="Document"
      required
      error={error}
      hint="PDF or photo, up to 10 MB. Only our team sees it."
    >
      <FileRow>
        <HiddenFileInput
          ref={input}
          type="file"
          accept="application/pdf,image/jpeg,image/png,image/webp,image/heic"
          onChange={event => {
            onFile(event.target.files?.[0] ?? null);
            if (input.current) input.current.value = '';
          }}
          data-testid={testId}
        />
        <Button
          type="button"
          theme="secondary"
          size="sm"
          text="Choose file"
          onClick={() => input.current?.click()}
        />
        <Muted $size="sm">{file ? file.name : 'No file chosen'}</Muted>
      </FileRow>
    </Field>
  );
}

function usePresign() {
  const [presign] = useGetPresignedUrlForServiceDocumentMutation();
  return async (contentType: string, contentLength: number) => {
    const { data } = await presign({ variables: { contentType, contentLength } });
    if (!data?.getPresignedUrlForServiceDocument) throw new Error('Could not start the upload');
    return data.getPresignedUrlForServiceDocument;
  };
}

export function InsuranceCard() {
  const providerQuery = useMyServiceProviderQuery({ fetchPolicy: 'cache-and-network' });
  const provider = providerQuery.data?.myServiceProvider ?? null;
  const presign = usePresign();
  const [submit, { loading }] = useSubmitProviderInsuranceMutation({
    refetchQueries: [{ query: MY_SERVICE_PROVIDER }],
  });
  const [open, setOpen] = useState(false);
  const [carrier, setCarrier] = useState('');
  const [policyNumber, setPolicyNumber] = useState('');
  const [coverage, setCoverage] = useState('');
  const [expiresOn, setExpiresOn] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [uploading, setUploading] = useState(false);

  const status = provider?.insuranceVerified
    ? {
        label: `Verified until ${formatDate(provider.insuranceExpiresAt)}`,
        tone: 'success' as const,
      }
    : provider?.insurancePending
      ? { label: 'Waiting for review', tone: 'warning' as const }
      : { label: 'Not added', tone: 'neutral' as const };

  const onSubmit = async () => {
    const found: Record<string, string> = {};
    if (!carrier.trim()) found.carrier = 'Enter the insurance company';
    if (!/^\d{4}-\d{2}-\d{2}$/.test(expiresOn) || DateTime.fromISO(expiresOn) <= DateTime.now()) {
      found.expiresOn = 'Pick a date in the future';
    }
    const coverageAmount = coverage.trim() ? Number(coverage) : null;
    if (coverageAmount !== null && !(coverageAmount > 0))
      found.coverage = 'Enter an amount, or leave empty';
    const fileProblem = documentFileProblem(file);
    if (fileProblem) found.file = fileProblem;
    setErrors(found);
    if (Object.keys(found).length) return;
    setUploading(true);
    try {
      const fileKey = await uploadServiceDocument(file!, presign);
      await submit({
        variables: {
          input: {
            carrier: carrier.trim(),
            policyNumber: policyNumber.trim() || null,
            coverageAmount,
            expiresAt: DateTime.fromISO(expiresOn).endOf('day').toISO()!,
            fileKey,
          },
        },
      });
      toast.success('Insurance sent for review');
      setOpen(false);
      setFile(null);
    } catch (error) {
      setErrors({ form: serviceErrorMessage(error, 'Could not send the insurance') });
    } finally {
      setUploading(false);
    }
  };

  return (
    <Card data-testid="insurance-card">
      <CardHeader
        title="Insurance"
        description="Covers all your workers. Customers see an Insured badge once we verify it."
        actions={
          <Badge tone={status.tone} data-testid="insurance-status">
            {status.label}
          </Badge>
        }
      />
      <CardBody>
        <Stack $gap={4}>
          {!provider?.insuranceVerified && (
            <Muted $size="sm">
              Without insurance, customers are told that damage claims go to you through the
              Resolution Center.
            </Muted>
          )}
          {!open ? (
            <div>
              <Button
                type="button"
                theme="secondary"
                size="sm"
                text={
                  provider?.insuranceVerified || provider?.insurancePending
                    ? 'Send a new policy'
                    : 'Add insurance'
                }
                onClick={() => setOpen(true)}
                dataTestId="insurance-open"
              />
            </div>
          ) : (
            <Stack $gap={4}>
              {errors.form && (
                <Alert tone="error" title="Not sent">
                  {errors.form}
                </Alert>
              )}
              <FormGrid>
                <Field
                  label="Insurance company"
                  htmlFor="ins-carrier"
                  required
                  error={errors.carrier}
                >
                  <Input
                    id="ins-carrier"
                    value={carrier}
                    onChange={e => setCarrier(e.target.value)}
                  />
                </Field>
                <Field label="Policy number" htmlFor="ins-policy" hint="Optional">
                  <Input
                    id="ins-policy"
                    value={policyNumber}
                    onChange={e => setPolicyNumber(e.target.value)}
                  />
                </Field>
                <Field
                  label="Coverage amount"
                  htmlFor="ins-coverage"
                  hint="Optional"
                  error={errors.coverage}
                >
                  <MoneyInput id="ins-coverage" value={coverage} onChange={setCoverage} />
                </Field>
                <Field label="Expires on" htmlFor="ins-expires" required error={errors.expiresOn}>
                  <Input
                    id="ins-expires"
                    type="date"
                    value={expiresOn}
                    onChange={e => setExpiresOn(e.target.value)}
                  />
                </Field>
              </FormGrid>
              <DocumentPicker
                file={file}
                onFile={setFile}
                error={errors.file}
                testId="insurance-file"
              />
              <FileRow>
                <Button
                  type="button"
                  text="Send for review"
                  onClick={onSubmit}
                  isLoading={uploading || loading}
                  dataTestId="insurance-submit"
                />
                <Button type="button" theme="text" text="Cancel" onClick={() => setOpen(false)} />
              </FileRow>
            </Stack>
          )}
        </Stack>
      </CardBody>
    </Card>
  );
}

export function BackgroundCheckCard({ worker }: { worker: ProviderWorker }) {
  const presign = usePresign();
  const [submit, { loading }] = useSubmitWorkerBackgroundCheckMutation();
  const check = worker.settings.backgroundCheck;
  const [open, setOpen] = useState(false);
  const [vendor, setVendor] = useState('Checkr');
  const [completedOn, setCompletedOn] = useState('');
  const [expiresOn, setExpiresOn] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [uploading, setUploading] = useState(false);

  const expired = check?.expiresAt ? DateTime.fromISO(check.expiresAt) <= DateTime.now() : false;
  const status = !check
    ? { label: 'Not added', tone: 'neutral' as const }
    : expired
      ? { label: 'Expired', tone: 'error' as const }
      : check.verified
        ? {
            label: check.expiresAt ? `Verified until ${formatDate(check.expiresAt)}` : 'Verified',
            tone: 'success' as const,
          }
        : { label: 'Waiting for review', tone: 'warning' as const };

  const onSubmit = async () => {
    const found: Record<string, string> = {};
    if (!vendor.trim()) found.vendor = 'Enter who ran the check';
    if (
      !/^\d{4}-\d{2}-\d{2}$/.test(completedOn) ||
      DateTime.fromISO(completedOn) > DateTime.now()
    ) {
      found.completedOn = 'Pick the date the check was finished';
    }
    if (expiresOn && DateTime.fromISO(expiresOn) <= DateTime.now())
      found.expiresOn = 'Pick a date in the future';
    const fileProblem = documentFileProblem(file);
    if (fileProblem) found.file = fileProblem;
    setErrors(found);
    if (Object.keys(found).length) return;
    setUploading(true);
    try {
      const fileKey = await uploadServiceDocument(file!, presign);
      await submit({
        variables: {
          workerCarId: worker.carId,
          input: {
            vendor: vendor.trim(),
            completedAt: DateTime.fromISO(completedOn).toISO()!,
            expiresAt: expiresOn ? DateTime.fromISO(expiresOn).endOf('day').toISO() : null,
            fileKey,
          },
        },
      });
      toast.success('Background check sent for review');
      setOpen(false);
      setFile(null);
    } catch (error) {
      setErrors({ form: serviceErrorMessage(error, 'Could not send the background check') });
    } finally {
      setUploading(false);
    }
  };

  return (
    <Card data-testid="background-check-card">
      <CardHeader
        title={`Background check for ${worker.worker.displayName}`}
        description="Upload the report (for example from Checkr). Needed to offer handovers."
        actions={
          <Badge tone={status.tone} data-testid="background-check-status">
            {status.label}
          </Badge>
        }
      />
      <CardBody>
        {!open ? (
          <Button
            type="button"
            theme="secondary"
            size="sm"
            text={check ? 'Send a new report' : 'Add a background check'}
            onClick={() => setOpen(true)}
            dataTestId="background-check-open"
          />
        ) : (
          <Stack $gap={4}>
            {errors.form && (
              <Alert tone="error" title="Not sent">
                {errors.form}
              </Alert>
            )}
            <FormGrid $cols={3}>
              <Field label="Checked by" htmlFor="bg-vendor" required error={errors.vendor}>
                <Input id="bg-vendor" value={vendor} onChange={e => setVendor(e.target.value)} />
              </Field>
              <Field label="Finished on" htmlFor="bg-completed" required error={errors.completedOn}>
                <Input
                  id="bg-completed"
                  type="date"
                  value={completedOn}
                  onChange={e => setCompletedOn(e.target.value)}
                />
              </Field>
              <Field
                label="Expires on"
                htmlFor="bg-expires"
                hint="Optional"
                error={errors.expiresOn}
              >
                <Input
                  id="bg-expires"
                  type="date"
                  value={expiresOn}
                  onChange={e => setExpiresOn(e.target.value)}
                />
              </Field>
            </FormGrid>
            <DocumentPicker
              file={file}
              onFile={setFile}
              error={errors.file}
              testId="background-check-file"
            />
            <FileRow>
              <Button
                type="button"
                text="Send for review"
                onClick={onSubmit}
                isLoading={uploading || loading}
                dataTestId="background-check-submit"
              />
              <Button type="button" theme="text" text="Cancel" onClick={() => setOpen(false)} />
            </FileRow>
          </Stack>
        )}
      </CardBody>
    </Card>
  );
}

export function CredentialsSection({ worker }: { worker: ProviderWorker }) {
  return (
    <Stack $gap={6}>
      <InsuranceCard />
      <BackgroundCheckCard worker={worker} />
    </Stack>
  );
}
